// CVv2 tags from ingestion: SEVERITY[_ETIOLOGY]_CONDITION, e.g.
// MODERATE_TO_SEVERE_SECONDARY_MITRAL_REGURGITATION (AP-964). They read like
// the worksheet statements ("Moderate-to-severe secondary mitral
// regurgitation") and have a short form for tight spaces ("Mod–sev secondary MR").
// `rank` sorts most severe first.
const CV2_SEVERITIES: Record<
  string,
  { full: string; short: string; rank: number }
> = {
  MODERATE: { full: 'Moderate', short: 'Moderate', rank: 1 },
  MODERATE_TO_SEVERE: { full: 'Moderate-to-severe', short: 'Mod–sev', rank: 2 },
  SEVERE: { full: 'Severe', short: 'Severe', rank: 3 },
};
const CV2_CONDITIONS: Record<string, { full: string; short: string }> = {
  MITRAL_REGURGITATION: { full: 'mitral regurgitation', short: 'MR' },
  TRICUSPID_REGURGITATION: { full: 'tricuspid regurgitation', short: 'TR' },
  AORTIC_STENOSIS: { full: 'aortic stenosis', short: 'AS' },
  AORTIC_REGURGITATION: { full: 'aortic regurgitation', short: 'AR' },
};
const CV2_TAG =
  /^(MODERATE_TO_SEVERE|MODERATE|SEVERE)_(?:(PRIMARY|SECONDARY)_)?(MITRAL_REGURGITATION|TRICUSPID_REGURGITATION|AORTIC_STENOSIS|AORTIC_REGURGITATION)$/;

function parseCv2Tag(tag: string) {
  const match = CV2_TAG.exec(tag);
  if (!match) return null;
  const [, severity, etiology, condition] = match;
  return {
    etiology: etiology?.toLowerCase(),
    sev: CV2_SEVERITIES[severity],
    cond: CV2_CONDITIONS[condition],
  };
}

/** How severe a CVv2 tag is, for sorting: 3 severe, 2 moderate-to-severe,
 *  1 moderate, 0 any other tag. */
export function tagSeverityRank(tag: string): number {
  return parseCv2Tag(tag)?.sev.rank ?? 0;
}

/**
 * "AORTIC_STENOSIS" → "AS", "FOLLOW_UP_RECOMMENDED_3_MONTHS" → "FUR3M".
 * A CVv2 tag gets its short label instead: "SEVERE_AORTIC_STENOSIS" → "Severe AS".
 */
export function abbreviateTag(tag: string): string {
  const cv2 = parseCv2Tag(tag);
  if (cv2) {
    return [cv2.sev.short, cv2.etiology, cv2.cond.short]
      .filter(Boolean)
      .join(' ');
  }
  return tag
    .split('_')
    .map((word) => (/^\d+$/.test(word) ? word : word.charAt(0)))
    .join('');
}

/**
 * "AORTIC_STENOSIS" → "Aortic Stenosis".
 * A CVv2 tag reads like its statement: "SEVERE_PRIMARY_MITRAL_REGURGITATION"
 * → "Severe primary mitral regurgitation".
 */
export function formatTagLabel(tag: string): string {
  const cv2 = parseCv2Tag(tag);
  if (cv2) {
    return [cv2.sev.full, cv2.etiology, cv2.cond.full]
      .filter(Boolean)
      .join(' ');
  }
  return tag
    .split('_')
    .map((word) => {
      if (/^\d+$/.test(word)) return word;
      return word.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(' ');
}
